import React, { useState, useEffect } from 'react';
import { PowerBIEmbed } from 'powerbi-client-react';
import { models } from 'powerbi-client';
import { Amplify } from 'aws-amplify';
import {
  Authenticator,
  ThemeProvider,
  useTheme,
  View,
  Text,
  Heading,
  Button
} from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import Sidebar from './components/Sidebar';
import ChatBot from './components/ChatBot';
import BookmarkManager from './components/BookmarkManager';
import AdminPanel from './components/AdminPanel';
import { ToastProvider } from './components/Toast';
import awsconfig from './amplifyconfiguration.json';
import logo from './assets/img/logo-data-new.png';
import './App.css';

const BACKEND_URL = 'https://al-bcd27e7e0130466f87e7390ee3784d32.ecs.us-east-1.on.aws';

// Configurar Amplify
Amplify.configure(awsconfig);

// Componentes personalizados del login
const components = {
  Header() {
    const { tokens } = useTheme();

    return (
      <View textAlign="center" padding={tokens.space.large}>
        <img src={logo} alt="Logo" className="login-logo" style={{ maxWidth: '200px', height: 'auto' }} />
      </View>
    );
  },

  Footer() {
    const { tokens } = useTheme();

    return (
      <View textAlign="center" padding={tokens.space.large}>
        <Text color={tokens.colors.neutral[80]}>
          © All Rights Reserved
        </Text>
      </View>
    );
  },

  SignIn: {
    Header() {
      const { tokens } = useTheme();

      return (
        <Heading
          padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
          level={3}
        >

        </Heading>
      );
    },
    Footer() {
      const [showMessage, setShowMessage] = useState(false);
      const handleClick = () => {
        setShowMessage(!showMessage);
      };
      return (
        <View textAlign="center">
          <Button
            fontWeight="normal"
            onClick={handleClick}
            size="small"
            variation="link"
          >
            Forgot your Password?
          </Button>
          {showMessage && <p>Contact the DataBunker administrator</p>}
        </View>
      );
    },
  },

  ForgotPassword: {
    Header() {
      return (
        <Heading>
          Reset Password
        </Heading>
      );
    },
    Footer() {
      return <Text></Text>;
    },
  },
};

// Campos del formulario
const formFields = {
  signIn: {
    username: {
      placeholder: 'Enter your User',
      label: 'Username',
    },
    password: {
      placeholder: 'Enter your Password',
      label: 'Password',
    }
  },
  forgotPassword: {
    username: {
      placeholder: 'Enter your User',
      label: 'Username',
    },
  },
};

// Componente TopBar
function TopBar({
  onResetFilters,
  isResetting,
  onToggleSidebar,
  canResetFilters = true,
  onExportPDF,
  isExporting,
  onOpenBookmarks,
  lastRefreshDate
}) {
  // Formatear la fecha de última actualización (solo día)
  const formatLastRefresh = (dateString) => {
    if (!dateString) return null;

    try {
      const date = new Date(dateString);

      // Formato de fecha: DD/MM/YYYY (solo día, sin hora)
      const formattedDate = date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });

      return formattedDate;
    } catch (error) {
      console.error('Error formateando fecha:', error);
      return null;
    }
  };

  const refreshDate = formatLastRefresh(lastRefreshDate);

  return (
    <div className="top-bar">
      <div className="top-bar-content">
        <div className="top-bar-left">
          <button className="hamburger-button" onClick={onToggleSidebar} aria-label="Toggle menu">
            <span className="hamburger-icon">
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>
          <h1 className="top-bar-title">Pricing Analytics Engine</h1>

          {/* 🕒 Fecha de última actualización */}
          {refreshDate && (
            <div className="last-refresh-indicator" title={`Datos actualizados el ${refreshDate}`}>
              <svg
                className="refresh-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="23 4 23 10 17 10"></polyline>
                <polyline points="1 20 1 14 7 14"></polyline>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
              </svg>
              <span className="refresh-text">
                Actualizado: {refreshDate}
              </span>
            </div>
          )}
        </div>

        <div className="top-bar-right">

          {/* Botón Export PDF */}
          <button
            className="export-pdf-btn"
            onClick={onExportPDF}
            disabled={isExporting}
            title="Export to PDF"
          >
            {isExporting ? (
              <>
                <span className="spinner"></span>
                <span>Exporting...</span>
              </>
            ) : (
              <>
                <svg
                  className="export-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="12" y1="18" x2="12" y2="12" />
                  <line x1="9" y1="15" x2="12" y2="18" />
                  <line x1="15" y1="15" x2="12" y2="18" />
                </svg>
                <span>Export PDF</span>
              </>
            )}
          </button>

          {/* Botón Bookmarks - Guardar/Aplicar Filtros */}
          <button
            className="bookmarks-btn"
            onClick={onOpenBookmarks}
            title="Guardar o aplicar filtros guardados"
          >
            <svg
              className="bookmark-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
            </svg>
            <span>Mis Filtros</span>
          </button>

          {/* Botón Reset Filters - Condicional */}
          {canResetFilters && (
            <button
              className="reset-filters-btn"
              onClick={onResetFilters}
              disabled={isResetting}
              title="Reset All Filters"
            >
              {isResetting ? (
                <>
                  <span className="spinner"></span>
                  <span>Resetting...</span>
                </>
              ) : (
                <>
                  <svg
                    className="reset-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                    <path d="M21 3v5h-5" />
                    <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                    <path d="M3 21v-5h5" />
                  </svg>
                  <span>Reset Filters</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Componente principal de Power BI
function PowerBIApp({ user, signOut }) {
  // Estados existentes
  const [embedConfig, setEmbedConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  const [isReportReady, setIsReportReady] = useState(false);
  const [currentPage, setCurrentPage] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  // Estados para múltiples reportes
  const [availableReports, setAvailableReports] = useState([]);
  const [currentReportId, setCurrentReportId] = useState(null);
  const [lastRefreshDate, setLastRefreshDate] = useState(null);

  // Estados para features, organización y rol
  const [userOrganization, setUserOrganization] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [userFeatures, setUserFeatures] = useState({});

  const [isExporting, setIsExporting] = useState(false);

  // 🆕 Estados para retry automático
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryMessage, setRetryMessage] = useState('');

  // 🔖 Estado para BookmarkManager
  const [isBookmarkManagerOpen, setIsBookmarkManagerOpen] = useState(false);

  // 👔 Estado para AdminPanel
  const [isAdminPanelOpen, setIsAdminPanelOpen] = useState(false);

  // 👤 Imprimir usuario cuando entra
  useEffect(() => {
    if (user) {
  
    }
  }, [user]);

  // 📋 Obtener lista de reportes disponibles al cargar
  useEffect(() => {
    if (user?.username) {
      getAvailableReports(user.username);
    }
  }, [user]);

  // 🔄 Cargar el reporte cuando cambia currentReportId
  useEffect(() => {
    if (currentReportId && user?.username) {
      getEmbedToken(user.username, currentReportId);
    }
  }, [currentReportId]);

  // 📄 Cambiar página cuando el reporte esté listo
  useEffect(() => {
    if (isReportReady && report && currentPage) {
      changePage(currentPage);
    }
  }, [isReportReady]);

  // 🎨 Actualizar clase en el body cuando cambia el estado del sidebar
  useEffect(() => {
    if (isSidebarOpen) {
      document.body.classList.add('sidebar-open');
      document.body.classList.remove('sidebar-closed');
    } else {
      document.body.classList.add('sidebar-closed');
      document.body.classList.remove('sidebar-open');
    }

    return () => {
      document.body.classList.remove('sidebar-open', 'sidebar-closed');
    };
  }, [isSidebarOpen]);

  // 🎯 Ocultar banner de Power BI cuando el reporte está listo
  useEffect(() => {
    if (isReportReady && report) {
      changePage(currentPage);

      // Ocultar banner de prueba de Power BI
      const hideBanner = () => {
        const banner = document.querySelector('notification-bar, [data-testid="notification-bar"]');
        if (banner) {
          banner.style.display = 'none';
          banner.style.visibility = 'hidden';
          banner.style.height = '0';
          banner.style.overflow = 'hidden';
        }
      };

      // Ejecutar inmediatamente y después de un delay
      hideBanner();
      setTimeout(hideBanner, 1000);
      setTimeout(hideBanner, 3000);
    }
  }, [isReportReady]);

  // Función para exportar a PDF (usando print del navegador)
  const handleExportPDF = async () => {
    if (!report) {
      console.error('❌ Reporte no está disponible');
      return;
    }

    try {
      setIsExporting(true);


      // Capturar el estado actual del reporte (filtros aplicados)
      const capturedBookmark = await report.bookmarksManager.capture({
        personalizeVisuals: true,
        allPages: false
      });



      // Abrir el diálogo de impresión del navegador
      // Esta es la forma más directa en Power BI Embedded
      // El usuario puede guardar como PDF desde el diálogo
      await report.print();




      // Feedback visual
      setTimeout(() => {
        setIsExporting(false);
      }, 1000);

    } catch (err) {
      console.error('❌ Error al exportar:', err);

      // Fallback: solo abrir diálogo de impresión sin captura
      try {
      
        await report.print();
        setTimeout(() => {
          setIsExporting(false);
        }, 1000);
      } catch (printErr) {
        console.error('❌ Error con print:', printErr);
        alert('No se pudo abrir el diálogo de impresión. Por favor, usa Ctrl+P o contacta con soporte.');
        setIsExporting(false);
      }
    }
  };

  // 📋 Función para obtener lista de reportes disponibles
  const getAvailableReports = async (username) => {
    try {
 

      const response = await fetch(
        `${BACKEND_URL}/api/powerbi-embed?username=${username}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Error al obtener reportes: ${response.status}`);
      }

      const data = await response.json();

      // Guardar organización, rol y features
      if (data.organization) {
        setUserOrganization(data.organization);
      }

      if (data.role) {
        setUserRole(data.role);
      }

      if (data.features) {
        setUserFeatures(data.features);
      }

      if (data.reports && data.reports.length > 0) {
        setAvailableReports(data.reports);
        // Establecer el primer reporte como activo por defecto
        setCurrentReportId(data.reports[0].id);
   
      } else {
        throw new Error('No se encontraron reportes disponibles para este usuario');
      }
    } catch (err) {
      console.error('❌ Error al obtener reportes:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  // 🔐 Función para obtener el embed token de un reporte específico (MEJORADA)
  const getEmbedToken = async (username, reportId, isRetry = false) => {
    try {
      if (!isRetry) {
        setLoading(true);
        setIsReportReady(false);
        setReport(null);
        setRetryCount(0);
        setIsRetrying(false);
        setRetryMessage('');
      }

   

      const response = await fetch(
        `${BACKEND_URL}/api/powerbi-embed?username=${username}&reportId=${reportId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        
        // 🆕 Detectar si es error de límite de workers y es retryable
        const isWorkerLimitError = 
          errorData.code === 'PowerBISqlWorkersLimitReachedError' ||
          errorData.details?.retryable === true ||
          errorData.message?.includes('ocupado') ||
          errorData.message?.includes('busy');

        if (isWorkerLimitError && retryCount < 3) {
          // Auto-retry con backoff exponencial
          const waitTime = Math.pow(2, retryCount + 1) * 1000; // 2s, 4s, 8s
          const waitSeconds = Math.floor(waitTime / 1000);
          
          setRetryCount(prev => prev + 1);
          setIsRetrying(true);
          setRetryMessage(`Servidor ocupado. Reintentando automáticamente en ${waitSeconds} segundos... (Intento ${retryCount + 1}/3)`);
          

          
          setTimeout(() => {
            getEmbedToken(username, reportId, true);
          }, waitTime);
          
          return; // Salir de la función y esperar el retry
        }
        
        throw new Error(errorData.message || `Error al obtener token: ${response.status}`);
      }

      const data = await response.json();

      // Limpiar estados de retry si fue exitoso
      setRetryCount(0);
      setIsRetrying(false);
      setRetryMessage('');

      // Actualizar organización, rol y features si vienen en la respuesta
      if (data.organization) setUserOrganization(data.organization);
      if (data.role) setUserRole(data.role);
      if (data.features) setUserFeatures(data.features);

      // 🆕 Actualizar fecha de última actualización del dataset
      if (data.lastRefreshDate) {
        setLastRefreshDate(data.lastRefreshDate);
   
      }

      // 🆕 Mostrar si el token vino del cache
      if (data.fromCache) {
      
      }

      // Establecer la primera página del menú como página inicial
      if (data.menu && data.menu.length > 0) {
        setCurrentPage(data.menu[0].name);
      }

      setEmbedConfig({
        type: 'report',
        id: data.reportId,
        embedUrl: data.embedUrl,
        accessToken: data.accessToken,
        tokenType: models.TokenType.Embed,
        settings: {
          panes: {
            filters: {
              expanded: false,
              visible: false
            },
            pageNavigation: {
              visible: false
            }
          },
          background: models.BackgroundType.Transparent,
          layoutType: models.LayoutType.Custom,
          customLayout: {
            displayOption: models.DisplayOption.FitToWidth
          }
        }
      });

      setLoading(false);
  
   
      
    } catch (err) {
      console.error('❌ Error:', err);
      
      // Si ya reintentamos 3 veces o no es retryable, mostrar error final
      setError(err.message);
      setLoading(false);
      setIsRetrying(false);
      setRetryMessage('');
    }
  };

  // 🔄 Función para cambiar de reporte
  const handleReportChange = (reportId) => {
    if (reportId !== currentReportId) {

      setCurrentReportId(reportId);
    }
  };

  // 📄 Función para cambiar de página dentro del reporte
  const changePage = async (pageName) => {
    if (!report) {
      console.error('❌ Reporte no está disponible');
      return;
    }

    try {
      const pages = await report.getPages();
      const targetPage = pages.find(page => page.displayName === pageName);

      if (targetPage) {
        await targetPage.setActive();
        setCurrentPage(pageName);
      
      } else {
        console.error('❌ Página no encontrada:', pageName);
      }
    } catch (err) {
      console.error('❌ Error al cambiar página:', err);
    }
  };

  // 🔄 Función para resetear filtros (recarga la página para mantener filtros RLS)
  const handleResetFilters = async () => {
    // Verificar si el usuario tiene permiso para resetear filtros
    if (userFeatures.canResetFilters === false) {
      console.warn('⚠️ Usuario no tiene permiso para resetear filtros');
      return;
    }

    try {
      setIsResetting(true);
  

      // Recargar la página para restaurar el estado inicial
      // Esto mantiene los filtros RLS (Row-Level Security) configurados por el desarrollador
      // y solo elimina los filtros aplicados manualmente por el usuario
      window.location.reload();
    } catch (err) {
      console.error('❌ Error al resetear filtros:', err);
      setIsResetting(false);
    }
  };

  // 🔀 Función para toggle del sidebar
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  // 🔖 Función para abrir/cerrar BookmarkManager
  const handleOpenBookmarks = () => {
    setIsBookmarkManagerOpen(true);
  };

  const handleCloseBookmarks = () => {
    setIsBookmarkManagerOpen(false);
  };

  // 👔 Funciones para AdminPanel
  const handleOpenAdminPanel = () => {
    console.log('[App] handleOpenAdminPanel llamado. userRole:', userRole);
    setIsAdminPanelOpen(true);
  };

  const handleCloseAdminPanel = () => {
    setIsAdminPanelOpen(false);
  };

  return (
    <div className="App">
      <Sidebar
        onPageChange={changePage}
        currentPage={currentPage}
        onSignOut={signOut}
        user={user}
        isOpen={isSidebarOpen}
        toggleSidebar={toggleSidebar}
        availableReports={availableReports}
        currentReportId={currentReportId}
        onReportChange={handleReportChange}
        userOrganization={userOrganization}
        userRole={userRole}
        userFeatures={userFeatures}
        onAdminPanelOpen={handleOpenAdminPanel}
      />

      <div className="main-content">
        {/* TopBar */}
        {!loading && !error && (
          <TopBar
            onResetFilters={handleResetFilters}
            isResetting={isResetting}
            onToggleSidebar={toggleSidebar}
            canResetFilters={userFeatures.canResetFilters !== false}
            onExportPDF={handleExportPDF}
            isExporting={isExporting}
            onOpenBookmarks={handleOpenBookmarks}
            lastRefreshDate={lastRefreshDate}
          />
        )}

        {/* 🆕 Estado de carga con mensaje de retry */}
        {loading && (
          <div className="loading">
            <h2>{isRetrying ? '⏳ Reintentando...' : 'Cargando dashboard...'}</h2>
            <p>
              {isRetrying 
                ? retryMessage 
                : 'Obteniendo credenciales de Power BI'}
            </p>
            {isRetrying && (
              <div className="retry-progress">
                <div className="spinner"></div>
              </div>
            )}
          </div>
        )}

        {/* 🆕 Estado de error mejorado */}
        {error && !isRetrying && (
          <div className="error">
            <h2>❌ Error</h2>
            <p>{error}</p>
            <div className="error-actions">
              <button onClick={() => {
                setError(null);
                setRetryCount(0);
                if (user?.username && currentReportId) {
                  getEmbedToken(user.username, currentReportId);
                } else if (user?.username) {
                  getAvailableReports(user.username);
                }
              }}>
                🔄 Reintentar ahora
              </button>
              
              <button 
                onClick={() => {
                  setError(null);
                  setCurrentReportId(null);
                  getAvailableReports(user.username);
                }}
                style={{ marginLeft: '10px' }}
              >
                ↩️ Volver a reportes
              </button>
            </div>
            <p style={{ marginTop: '20px', fontSize: '0.9em', color: '#666' }}>
              Si el problema persiste, por favor contacta al administrador.
            </p>
          </div>
        )}

        {!loading && !error && embedConfig && (
          <div className="powerbi-container">
            <div className="powerbi-content-wrapper">
              <PowerBIEmbed
                embedConfig={embedConfig}

                eventHandlers={
                  new Map([
                    ['loaded', function () {
         
                    }],
                    ['rendered', function () {
              
                      setIsReportReady(true);
                    }],
                    ['error', function (event) {
                      // 🛠️ CORRECCIÓN: Filtrar errores no críticos
                      const errorDetail = event.detail;
                      const errorMessage = errorDetail?.message || '';
                      const errorLevel = errorDetail?.level;
                      
                      // Lista de errores que NO son críticos y podemos ignorar
                      const nonCriticalErrors = [
                        'visualClickedFailed',
                        'visualClicked',
                        'dataSelected',
                        'commandTriggered'
                      ];

                      // Solo registrar en consola si no es crítico
                      if (nonCriticalErrors.includes(errorMessage)) {
                        console.warn('⚠️ Evento de Power BI (no crítico):', errorMessage);
                        return; // No hacer nada más
                      }

                      // Si el nivel de error es bajo (nivel 1-2), solo advertir
                      if (errorLevel && errorLevel <= 2) {
                        console.warn('⚠️ Advertencia de Power BI:', errorDetail);
                        return;
                      }

                      // Solo mostrar errores realmente críticos
                      console.error('❌ Error crítico en el reporte:', errorDetail);
                      
                      // Solo establecer error de UI si es realmente grave
                      if (errorLevel && errorLevel >= 3) {
                        setError('Error crítico al cargar el reporte de Power BI. Por favor, recarga la página.');
                      }
                    }]
                  ])
                }

                cssClassName="powerbi-report"

                getEmbeddedComponent={(embeddedReport) => {
                  setReport(embeddedReport);
                  window.report = embeddedReport;
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* BookmarkManager Modal */}
      <BookmarkManager
        report={report}
        username={user?.username}
        reportId={currentReportId}
        isVisible={isBookmarkManagerOpen}
        onClose={handleCloseBookmarks}
      />

      {/* AdminPanel - Solo para Admins */}
      {isAdminPanelOpen && userRole?.toLowerCase() === 'admin' && (
        <AdminPanel
          username={user?.username}
          onClose={handleCloseAdminPanel}
          apiUrl={BACKEND_URL}
        />
      )}

      {/* ChatBot condicional basado en features */}
      {userFeatures.showChatBot !== false && <ChatBot username={user?.username} />}
    </div>
  );
}

// App principal con autenticación
function App() {
  const theme = {
    name: 'DataBunker Theme',
    tokens: {
      colors: {
        brand: {
          primary: {
            10: '#062341',
            80: '#073C5C',
            90: '#30A7B5',
            100: '#F68D2E',
          },
        },
      },
      components: {
        authenticator: {
          router: {
            boxShadow: '0 8px 24px rgba(6, 35, 65, 0.12)',
            borderWidth: '0',
            borderRadius: '16px',
          },
          form: {
            padding: '20px 40px 40px 40px',
          },
        },
        button: {
          primary: {
            backgroundColor: '#062341',
            _hover: {
              backgroundColor: '#073C5C',
            },
            _focus: {
              backgroundColor: '#073C5C',
            },
            _active: {
              backgroundColor: '#062341',
            },
          },
          link: {
            color: '#30A7B5',
            _hover: {
              color: '#073C5C',
            },
          },
        },
        fieldcontrol: {
          borderColor: '#e2e8f0',
          _focus: {
            borderColor: '#30A7B5',
            boxShadow: '0 0 0 3px rgba(48, 167, 181, 0.1)',
          },
        },
        tabs: {
          item: {
            color: '#718096',
            borderColor: 'transparent',
            _active: {
              borderColor: '#30A7B5',
              color: '#062341',
            },
            _hover: {
              color: '#062341',
            },
          },
        },
      },
    },
  };

  return (
    <ThemeProvider theme={theme}>
      <ToastProvider>
        <View className="authenticator-container">
          <Authenticator
            hideSignUp={true}
            formFields={formFields}
            components={components}
          >
            {({ signOut, user }) => (
              <PowerBIApp user={user} signOut={signOut} />
            )}
          </Authenticator>
        </View>
      </ToastProvider>
    </ThemeProvider>
  );
}

export default App;